import React from 'react';
import { builderPartners } from '../../data/mock';

/**
 * BuilderPartnersSection (Homepage)
 *
 * Visually mirrors AllPartnersSection used on /register-business so the
 * partner-network strip looks identical across the marketing site.
 * Combines builder logos with the broader pro-services partner list
 * (finance, legal, conveyancing, inspections, real-estate, design,
 * industry) into a 4-row marquee.
 *
 * Builder logos render edge-to-edge inside the circle (object-cover)
 * because those tiles are full-bleed square packs; pro-services logos
 * render with padding (object-contain) because they are wordmarks that
 * need breathing room.
 */

const taggedBuilders = builderPartners.map((p) => ({ ...p, kind: 'builder' }));

const taggedPros = [
  { name: 'JetFuel', logo: '/images/partners/jetfuel.png' },
  { name: 'HIA', logo: '/images/partners/hia.png' },
  { name: 'Homely', logo: '/images/partners/homely.png' },
  { name: 'Domain', logo: '/images/partners/domain.png' },
  { name: 'Houzz', logo: '/images/partners/houzz.png' },
  { name: 'Elite Agent', logo: '/images/partners/eliteagent.png' },
  { name: 'Loan Market Vantage', logo: '/images/partners/loanmarket.png' },
  { name: 'Equable', logo: '/images/partners/equable.png' },
  { name: 'Century 21', logo: '/images/partners/century21.jpg' },
  { name: 'Alexander Symons', logo: '/images/partners/alexander.jpg' },
  { name: 'Lanser', logo: '/images/partners/lanser.png' },
  { name: 'House Inspect Australia', logo: '/images/partners/houseinspect.png' },
  { name: 'Michael Kris Real Estate', logo: '/images/partners/mkre.png' },
  { name: 'Connekt', logo: '/images/partners/connekt.jpg' },
  { name: 'Magnolia Estates', logo: '/images/partners/magnolia.webp' },
  { name: 'CPC Lawyers', logo: '/images/partners/cpclawyers.png' },
  { name: 'Ray White Prospect', logo: '/images/partners/raywhite.png' },
  { name: 'Noakes Nickolas', logo: '/images/partners/noakesnickolas.png' },
  { name: 'Clarico', logo: '/images/partners/clarico.png' },
  { name: 'Davin Real Estate', logo: '/images/partners/davin.png' },
  { name: 'YPC Conveyancing', logo: '/images/partners/ypc.png' },
  { name: 'Plungie', logo: '/images/partners/plungie.png' },
  { name: 'PayLocker', logo: '/images/partners/paylocker-icon.png' },
].map((p) => ({ ...p, kind: 'pro' }));

// Fisher-Yates shuffle so each load mixes builder + pro logos together.
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const all = shuffleArray([...taggedBuilders, ...taggedPros]);

// Split as evenly as possible into 4 rows so each logo appears once per row.
const rowCount = 4;
const perRow = Math.ceil(all.length / rowCount);
const rows = Array.from({ length: rowCount }, (_, i) =>
  all.slice(i * perRow, (i + 1) * perRow)
);

const MarqueeRow = ({ items, direction = 'left', speed = 55 }) => {
  // Duplicate once for seamless loop (2 copies total).
  const duplicated = [...items, ...items];

  return (
    <div className="relative overflow-hidden py-4 sm:py-5">
      <div
        className={`flex gap-12 sm:gap-16 lg:gap-20 ${direction === 'left' ? 'animate-marquee-left' : 'animate-marquee-right'}`}
        style={{ width: 'max-content', animationDuration: `${speed}s` }}
      >
        {duplicated.map((p, i) => {
          const isBuilder = p.kind === 'builder';
          return (
            <div
              key={`${p.name}-${i}`}
              className={`flex-shrink-0 w-[64px] h-[64px] sm:w-[78px] sm:h-[78px] lg:w-[92px] lg:h-[92px] rounded-full overflow-hidden bg-white ring-1 ring-black/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.08)] hover:ring-black/[0.12] transition-all duration-300 ${
                isBuilder ? '' : 'flex items-center justify-center p-3 sm:p-3.5 lg:p-4'
              }`}
            >
              <img
                src={p.logo}
                alt={`${p.name} - BuildPilot partner`}
                width="92"
                height="92"
                loading="eager"
                decoding="async"
                className={isBuilder ? 'w-full h-full object-cover' : 'max-w-full max-h-full object-contain'}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BuilderPartnersSection = () => {
  return (
    <section
      id="builder-partners"
      className="py-7 sm:py-14 lg:py-20 bg-white overflow-hidden"
      data-testid="home-partners-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-10 lg:mb-12">
        <h2 className="text-[26px] sm:text-[34px] lg:text-[40px] font-semibold text-[#1d1d1f] text-center leading-[1.1] tracking-[-0.02em]">
          <span className="gradient-text-orange">Partnered</span> with Australia's<br className="sm:hidden" /> Leading Companies
        </h2>
        <p className="text-center text-[#86868b] mt-4 text-base sm:text-lg max-w-2xl mx-auto">
          We made a promise on day one: every business we onboard has to be genuinely connected to the building industry.
        </p>
      </div>

      {/* 4 alternating rows. Speeds are slightly staggered so rows
          don't sync up and create a visible "lane" effect. */}
      <div className="space-y-2 sm:space-y-4">
        <MarqueeRow items={rows[0]} direction="left"  speed={55} />
        <MarqueeRow items={rows[1]} direction="right" speed={60} />
        <MarqueeRow items={rows[2]} direction="left"  speed={65} />
        <MarqueeRow items={rows[3]} direction="right" speed={58} />
      </div>
    </section>
  );
};

export default BuilderPartnersSection;
